import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

interface ModalProps {
    isOpen: boolean;
    title: string;
    content: string; // Markdown content
    onAccept: () => void;
    onDecline: () => void;
    acceptButtonText?: string;
    declineButtonText?: string;
    requiresScrollToBottom?: boolean; // If true, user must scroll to bottom before accepting
    alreadyAcceptedMessage?: string; // Message shown if user has already accepted (e.g., "You have already accepted...")
    isAlreadyAccepted?: boolean; // Whether to show "already accepted" state
    showAgreementCheckbox?: boolean; // Whether to show the "I have read and agree" checkbox (default: true)
}

/**
 * Generic modal component for displaying markdown content with accept/decline actions.
 * Useful for ToS, disclaimers, confirmations, etc.
 */
export const Modal: React.FC<ModalProps> = ({
    isOpen,
    title,
    content,
    onAccept,
    onDecline,
    acceptButtonText = 'Accept',
    declineButtonText = 'Decline',
    requiresScrollToBottom = true,
    alreadyAcceptedMessage = 'You have already accepted',
    isAlreadyAccepted = false,
    showAgreementCheckbox = true
}) => {
    const [hasAccepted, setHasAccepted] = useState(false);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(!requiresScrollToBottom);
    const contentRef = React.useRef<HTMLDivElement>(null);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!requiresScrollToBottom) return;
        
        const element = e.currentTarget;
        const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 5; // 5px tolerance
        setHasScrolledToBottom(isAtBottom);
    };

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setHasAccepted(false);
            setHasScrolledToBottom(!requiresScrollToBottom);
        }
    }, [isOpen, requiresScrollToBottom]);

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        }}>
            <div style={{
                backgroundColor: '#1a1a1a',
                borderRadius: '8px',
                border: '1px solid #555',
                width: '90%',
                maxWidth: '600px',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px',
                    borderBottom: '1px solid #555',
                    flexShrink: 0
                }}>
                    <h2 style={{ margin: 0 }}>{title}</h2>
                </div>

                {/* Content */}
                <div 
                    ref={contentRef}
                    onScroll={handleScroll}
                    style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '20px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                }}>
                    <div style={{ margin: 0, textAlign: 'left' }}>
                        <ReactMarkdown>
                            {content}
                        </ReactMarkdown>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '20px',
                    borderTop: '1px solid #555',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    gap: '10px'
                }}>
                    {isAlreadyAccepted ? (
                        <>
                            <span style={{ color: '#65ff65ff', fontSize: '14px' }}>
                                ✓ {alreadyAcceptedMessage}
                            </span>
                            <button
                                onClick={onDecline}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#555',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Close
                            </button>
                        </>
                    ) : showAgreementCheckbox ? (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: hasScrolledToBottom ? '#ddd' : '#888' }}>
                                    <input
                                        type="checkbox"
                                        checked={hasAccepted}
                                        onChange={(e) => setHasAccepted(e.target.checked)}
                                        disabled={!hasScrolledToBottom}
                                        style={{ cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed' }}
                                    />
                                    I have read and agree
                                </label>
                                {requiresScrollToBottom && !hasScrolledToBottom && (
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        Please scroll to the bottom to enable
                                    </span>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={onDecline}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#555',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    {declineButtonText}
                                </button>
                                <button
                                    onClick={onAccept}
                                    disabled={!hasAccepted}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: hasAccepted ? '#65ff65ff' : '#999',
                                        color: hasAccepted ? '#000' : '#aaa',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: hasAccepted ? 'pointer' : 'not-allowed',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {acceptButtonText}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div />
                            <button
                                onClick={onAccept}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#65ff65ff',
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}
                            >
                                {acceptButtonText}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
